import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import Skeleton from 'react-loading-skeleton';
import { STRIPE_PUBLIC_KEY } from '../../general/config';
import { API_COMMON_URL } from '../../redux/constants/constants';
import { sessionKey, paymentPageTokenSelector } from '../../redux/selectors/pagesSelectors';
import { useHeaderSlot } from '../../context/HeaderSlotContext';
import RegisterBlock from '../../components/Registraion/RegisterBlock';
import useQuery from '../../hooks/useQuery';
import CheckoutForm from '../../components/CheckoutForm';

import './additionalPackage.scss';
import { PAYMENT_PAYPAL_ENABLED } from "../../constants/constants";
import { ReactComponent as PayPalLogo } from '../../svg/filled/paypal.svg';
import { checkout } from "../../redux/actions/checkout";

export default function AdditionalPackage() {
    const dispatch = useDispatch();
    const token = useSelector(sessionKey);
    const payment_page_token = useSelector(paymentPageTokenSelector);
    const { setHeaderContent } = useHeaderSlot();
    const query = useQuery();

    const [loading, setLoading] = useState(true);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [productId, setProductId] = useState(null);
    const [productInfo, setProductInfo] = useState({ comment: '' });
    const [stripe, setStripe] = useState(null);
    const [clientSecret, setClientSecret] = useState(null);
    const [errorResponse, setErrorResponse] = useState(true);
    const [products, setProducts] = useState([]);
    const [extraPackageDescription, setExtraPackageDescription] = useState([]);

    // Initialize Stripe only once
    const stripePromise = useMemo(() => loadStripe(STRIPE_PUBLIC_KEY), []);

    // Setup header block
    useEffect(() => {
        setHeaderContent(<RegisterBlock />);
        return () => setHeaderContent(null);
    }, [setHeaderContent]);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await fetch(API_COMMON_URL + '?method_name=product_tariffs_get');
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                const data = await response.json();
                setProducts(data?.data?.products ?? []);
            } catch (error) {
                /* console.error('Ошибка при загрузке продуктов:', error); */
            }
        };

        fetchProducts().then(r => {

        });

    }, []);

    useEffect(() => {
        setExtraPackageDescription(products[productId]?.extra_package_description_items ?? [])

    }, [products, productId])

    // Get product_id from query
    useEffect(() => {
        setProductId(query.get('product_id'));
    }, [query]);

    // Load Stripe instance
    useEffect(() => {
        stripePromise.then(setStripe);
    }, [stripePromise]);

    // Fetch package information from API
    useEffect(() => {

        if (!productId) return;

        const postData = {
            'method_name': 'bill_package_pay',
            'product_id': productId,
            'get_bill_only': 1,
            'invoice_source': 'UPP',
            'user_token': token,
            'payment_page_token': payment_page_token,
        };

        const queryString = new URLSearchParams(postData).toString();

        dispatch({
            CallAPI: `${API_COMMON_URL}?${queryString}`,
            showError: true,
        }).then((data) => {
            if (data?.apiData?.error_no === 409) {
                setProductInfo(prev => ({
                    ...prev,
                    comment: data?.apiData?.error_message ?? '',
                }));
                setErrorResponse(true);
                return;
            }

            if (data?.apiData?.data?.operation_status === 'SUCCESS') {
                setProductInfo(data?.apiData?.data ?? {});
                setErrorResponse(false);
                return;
            }

            setProductInfo(prev => ({
                ...prev,
                comment: 'This link is incorrect.',
            }));

        }).finally(() => {
            setLoading(false);
        });

    }, [token, payment_page_token, dispatch, productId]);

    // Fetch payment information from API
    useEffect(() => {

        setCheckoutLoading(true);

        if (productId === null) return;

        if (clientSecret) {
            setCheckoutLoading(false);
            return;
        }

        const postData = {
            'method_name': 'bill_checkout',
            'product_id': productId,
            'user_token': token,
            'payment_page_token': payment_page_token,
            'currency': 'USD',
            'extra_package_only': 1,
            'ui_mode': 'embedded',
        };

        dispatch({
            CallAPI: API_COMMON_URL,
            showError: true,
            postData,
        }).then((data) => {
            console.log(data)
            if (data?.apiData?.data?.operation_status === 'SUCCESS') {
                setClientSecret(data?.apiData?.data?.client_secret ?? null);
            }

        }).finally(() => {
            setTimeout(() => {
                setLoading(false);
                setCheckoutLoading(false);
            }, 2000)
        });

    }, [productId]);

    useEffect(() => {
        if (!stripe) {
            const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);
            stripePromise.then(setStripe);
        }
    }, [stripe]);

    if (loading) {
        return (
            <div className="additional-package">
                <Skeleton height={230} width={680} style={{ marginBottom: 10 }}/>
            </div>
        );
    }

    const handlePayPal = () => {

        const postData = {
            'method_name': 'bill_checkout',
            'product_id': productId,
            'payment_page_token': payment_page_token,
            'currency': 'USD',
            'extra_package_only': 1,
            'ui_mode': 'embedded',
            'cancel_url': window.location.href,
        }

        dispatch(checkout('paypal', postData));
    };

    return (
        <>
            <div className="additional-package">
                <div className="additional-package__card">
                    <h2 className="additional-package__title">Additional Package</h2>
                    <p className="additional-package__comment">{productInfo?.comment}</p>

                    {!errorResponse &&
                        <ul className='text--left'>
                            {extraPackageDescription?.map((item, index) => (
                                <li key={index + '_additional-package'}>{item}</li>
                            ))}
                        </ul>
                    }
                </div>
            </div>

            {!errorResponse &&
                <div className="additional-package__checkout-loading">
                    {/* Skeleton while Stripe checkout loads */}
                    {checkoutLoading && (
                        <Skeleton height={600} width={900} style={{ marginTop: 20 }}/>
                    )}

                    {/* Render Stripe checkout form when ready */}
                    {clientSecret && !checkoutLoading && (
                        <CheckoutForm
                            stripe={stripe}
                            pagesClientSecret={clientSecret}
                        />
                    )}
                    <div className={'card__or'}>OR</div>
                    <div className={`card__list card__list${PAYMENT_PAYPAL_ENABLED ? '' : '--single'}`}>
                        {!!PAYMENT_PAYPAL_ENABLED && (
                            <div className={'card__container'}>
                                <div className={''}>
                                    <h3 className={'card__title'}>Make a one time PayPal payment</h3>
                                    <span className={'card__description'}>
                                    It may take up to 5 minutes for the payment process
                                </span>
                                </div>
                                <div className={'card__item'}>
                                    <PayPalLogo className={'card__logo'}/>
                                </div>
                                <div className={'card__item'}>
                                    <button
                                        disabled={checkoutLoading}
                                        onClick={() => handlePayPal()}
                                        type={'button'}
                                        className={'button card__button button--proceed'}
                                    >
                                        <span className={'text-2 bold'}>Pay Now</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                </div>}

        </>
    );
}
